import { client } from "./supabase";

export const MAX_FILE_SIZE_FOTO = 4 * 1024 * 1024;
export const MAX_FILE_SIZE_VIDEO = 30 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const ACCEPTED_VIDEO_TYPES = ["video/mp4"];

// ---------------------------------------------------------------------------
// Clave de reporte
// ---------------------------------------------------------------------------
// Una fila de reportes_fotograficos pertenece a una venta directa { sale_id } O a
// una cuota de contrato { contrato_id, periodo }, nunca a ambas ni a ninguna. Esto
// espeja el CHECK chk_reporte_sale_xor_contrato de la DB (migración Fase 2a).

const esClaveVenta = (clave) => clave?.sale_id != null && clave?.contrato_id == null && clave?.periodo == null;

const esClaveContrato = (clave) =>
  clave?.sale_id == null && clave?.contrato_id != null && clave?.periodo != null && clave.periodo !== "";

// Devuelve "venta" | "contrato", o lanza si la clave no es exactamente una de las dos.
export const validarClaveReporte = (clave) => {
  if (esClaveVenta(clave)) return "venta";
  if (esClaveContrato(clave)) return "contrato";
  throw new Error("Clave de reporte inválida: debe ser exactamente { sale_id } o { contrato_id, periodo }.");
};

// Aplica el filtro de la clave a un query builder de supabase.
const filtrarPorClave = (query, clave) =>
  validarClaveReporte(clave) === "venta"
    ? query.eq("sale_id", clave.sale_id)
    : query.eq("contrato_id", clave.contrato_id).eq("periodo", clave.periodo);

// ---------------------------------------------------------------------------
// Núcleo de lectura (por clave)
// ---------------------------------------------------------------------------

// Media (fotos + videos) de una unidad de reporte, ordenada por idedificio y fecha.
export const getMediaByClave = async (clave) => {
  const { data, error } = await filtrarPorClave(client.from("reportes_fotograficos").select("*"), clave)
    .order("idedificio")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
};

// Filas existentes para un set de claves (para detectar unidades ya con reporte y
// resumir progreso/cronología). Devuelve { id, sale_id, contrato_id, periodo,
// idedificio, created_at } por fila.
export const getReportesByClaves = async (claves) => {
  if (!claves?.length) return [];
  const saleIds = [];
  const contratoPairs = [];
  claves.forEach((c) => {
    if (validarClaveReporte(c) === "venta") saleIds.push(c.sale_id);
    else contratoPairs.push({ contrato_id: c.contrato_id, periodo: c.periodo });
  });

  const filas = [];

  if (saleIds.length > 0) {
    const { data, error } = await client
      .from("reportes_fotograficos")
      .select("id, sale_id, contrato_id, periodo, idedificio, created_at")
      .in("sale_id", saleIds);
    if (error) throw error;
    filas.push(...(data || []));
  }

  if (contratoPairs.length > 0) {
    const contratoIds = [...new Set(contratoPairs.map((p) => p.contrato_id))];
    const { data, error } = await client
      .from("reportes_fotograficos")
      .select("id, sale_id, contrato_id, periodo, idedificio, created_at")
      .in("contrato_id", contratoIds);
    if (error) throw error;
    const pares = new Set(contratoPairs.map((p) => `${p.contrato_id}|${p.periodo}`));
    filas.push(...(data || []).filter((r) => pares.has(`${r.contrato_id}|${r.periodo}`)));
  }

  return filas;
};

// ---------------------------------------------------------------------------
// Adaptadores de lectura (firma y comportamiento EXACTOS del caso venta)
// ---------------------------------------------------------------------------

export const getFotosBySaleId = async (saleId) => getMediaByClave({ sale_id: saleId });

export const getMediaBySaleId = async (saleId) => getMediaByClave({ sale_id: saleId });

export const getReportesBySaleIds = async (saleIds) => {
  if (!saleIds?.length) return [];
  const filas = await getReportesByClaves(saleIds.map((sale_id) => ({ sale_id })));
  return filas.map((r) => ({ sale_id: r.sale_id, id: r.id }));
};

// ---------------------------------------------------------------------------
// Núcleo de escritura (por clave)
// ---------------------------------------------------------------------------

// Path de Storage por clave. Venta (sin cambios) usa carpeta numérica del sale_id;
// contrato usa "c{contrato_id}/{periodo}" para no colisionar con carpetas de venta.
const storagePathPorClave = (clave, idedificio, ext, ts) =>
  validarClaveReporte(clave) === "venta"
    ? `reportes/${clave.sale_id}/${idedificio}_${ts}.${ext}`
    : `reportes/c${clave.contrato_id}/${clave.periodo}/${idedificio}_${ts}.${ext}`;

// Columnas de identidad a insertar según la clave.
const columnasClave = (clave) =>
  validarClaveReporte(clave) === "venta"
    ? { sale_id: clave.sale_id }
    : { contrato_id: clave.contrato_id, periodo: clave.periodo };

// Sube un archivo e inserta la fila de reporte para la unidad indicada por `clave`.
export const uploadMediaReporte = async (file, { clave, idedificio, created_by }) => {
  const tipo = file.type === "video/mp4" ? "video" : "foto";
  const ext = file.name.split(".").pop().toLowerCase();
  const path = storagePathPorClave(clave, idedificio, ext, Date.now());
  const { error: uploadError } = await client.storage.from("galeria").upload(path, file);
  if (uploadError) throw uploadError;
  const { data: urlData } = client.storage.from("galeria").getPublicUrl(path);
  const url = urlData.publicUrl;
  const { data, error: insertError } = await client
    .from("reportes_fotograficos")
    .insert([{ ...columnasClave(clave), idedificio, url, created_by, tipo }])
    .select();
  if (insertError) throw insertError;
  return data?.[0];
};

// ---------------------------------------------------------------------------
// Adaptador de escritura (firma y comportamiento EXACTOS del caso venta)
// ---------------------------------------------------------------------------

export const uploadFotoReporte = async (file, { sale_id, idedificio, created_by }) =>
  uploadMediaReporte(file, { clave: { sale_id }, idedificio, created_by });

// Borra archivo de Storage + fila. El parseo captura todo lo que sigue a "/galeria/",
// por lo que reconoce AMBOS formatos de path (venta "reportes/{sale_id}/..." y
// contrato "reportes/c{contrato_id}/{periodo}/..."). El delete es por id (PK), agnóstico
// a la clave. Sirve para venta y para cuota de contrato sin ramificar.
export const deleteFotoReporte = async (fotoRecord) => {
  const url = new URL(fotoRecord.url);
  const pathMatch = url.pathname.match(/\/storage\/v1\/object\/public\/galeria\/(.+)/);
  if (pathMatch) {
    const storagePath = decodeURIComponent(pathMatch[1]);
    await client.storage.from("galeria").remove([storagePath]);
  }
  const { error } = await client.from("reportes_fotograficos").delete().eq("id", fotoRecord.id);
  if (error) throw error;
};
